import React, { useCallback, useEffect, useRef } from "react";
import styles from "./BookPreviewModal.module.css";
import "../i18n";
import { useTranslation } from 'react-i18next';

interface Props {
  isOpen: boolean;
  title: string;
  pages: string[];
  pageIndex: number;
  direction: "next" | "prev";
  onClose: () => void;
  onPrev: () => void;
  onNext: () => void;
}

const BookPreviewModal: React.FC<Props> = ({
  isOpen,
  title,
  pages,
  pageIndex,
  direction,
  onClose,
  onPrev,
  onNext,
}) => {
  const { t } = useTranslation();
  const flipLockRef = useRef(false);
  const flipTimerRef = useRef<number | null>(null);
  const queuedActionRef = useRef<null | (() => void)>(null);
  const pageIndexRef = useRef(pageIndex);
  const pagesLenRef = useRef(pages.length);
  const ANIMATION_MS = 420;

  const releaseFlipLock = () => {
    flipLockRef.current = false;
    if (flipTimerRef.current !== null) {
      window.clearTimeout(flipTimerRef.current);
      flipTimerRef.current = null;
    }
    const queued = queuedActionRef.current;
    queuedActionRef.current = null;
    if (queued) queued();
  };

  useEffect(() => {
    if (!isOpen) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [isOpen]);


  const hasPrev = pageIndex > 0;
  const hasNext = pageIndex < pages.length - 1;
  const current = pages[pageIndex];
  const prevIndexRef = useRef(pageIndex);
  const prevIndex = prevIndexRef.current;
  const prevPage = pages[prevIndex];

  useEffect(() => {
    prevIndexRef.current = pageIndex;
  }, [pageIndex]);

  useEffect(() => {
    pageIndexRef.current = pageIndex;
  }, [pageIndex]);

  useEffect(() => {
    pagesLenRef.current = pages.length;
  }, [pages.length]);

  useEffect(() => {
    if (!isOpen) {
      releaseFlipLock();
      return;
    }
    return () => releaseFlipLock();
  }, [isOpen]);

  const queueAction = React.useCallback((action: () => void) => {
    queuedActionRef.current = action;
  }, []);

  const withFlipLock = React.useCallback(
    (action: () => void) => {
      if (flipLockRef.current) {
        queueAction(action);
        return;
      }
      flipLockRef.current = true;
      action();
      flipTimerRef.current = window.setTimeout(() => {
        flipLockRef.current = false;
        flipTimerRef.current = null;
        const queued = queuedActionRef.current;
        queuedActionRef.current = null;
        if (queued) queued();
      }, ANIMATION_MS);
    },
    [queueAction, ANIMATION_MS],
  );

  const handlePrev = useCallback(() => {
    if (!hasPrev) return;
    const action = () => {
      if (pageIndexRef.current > 0) onPrev();
    };
    withFlipLock(action);
  }, [hasPrev, onPrev, withFlipLock]);

  const handleNext = useCallback(() => {
    if (!hasNext) return;
    const action = () => {
      if (pageIndexRef.current < pagesLenRef.current - 1) onNext();
    };
    withFlipLock(action);
  }, [hasNext, onNext, withFlipLock]);

  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
      if (event.key === "ArrowLeft") handlePrev();
      if (event.key === "ArrowRight") handleNext();
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [isOpen, onClose, handlePrev, handleNext]);

  useEffect(() => {
    if (!isOpen) return;
    const toPreload: string[] = [];
    if (pageIndex > 0) toPreload.push(pages[pageIndex - 1]);
    if (pageIndex < pages.length - 1) toPreload.push(pages[pageIndex + 1]);
    const imgs: HTMLImageElement[] = [];
    toPreload.forEach((src) => {
      if (!src) return;
      const img = new Image();
      img.src = src;
      imgs.push(img);
    });
    return () => {
      imgs.length = 0;
    };
  }, [isOpen, pageIndex, pages]);

  const pageKey = `${pageIndex}-${direction}`;
  const prevKey = `${prevIndex}-${direction}-out`;

  if (!isOpen) return null;


  return (
    <div className={styles.backdrop} onClick={onClose}>
      <div className={styles.modal} onClick={(event) => event.stopPropagation()}>
        <div className={styles.header}>
          <div className={styles.title}>{title}</div>
          <button className={styles.close} onClick={onClose} type="button" aria-label={t('app.close')}>
            ×
          </button>
        </div>
        <div className={styles.viewer}>
          <div className={styles.pageWrap}>
            {current ? (
              <div className={styles.pageScene}>
                {prevIndex !== pageIndex && prevPage && (
                  <img
                    key={prevKey}
                    src={prevPage}
                      alt={`${title} — ${t('page')} ${prevIndex + 1}`}
                    className={`${styles.page} ${styles.pageOutgoing} ${
                      direction === "next" ? styles.flipOutNext : styles.flipOutPrev
                    }`}
                            loading="lazy"
                  />
                )}
                <img
                  key={pageKey}
                  src={current}
                  alt={`${title} — ${t('page')} ${pageIndex + 1}`}
                  className={`${styles.page} ${styles.pageIncoming} ${
                    direction === "next" ? styles.flipInNext : styles.flipInPrev
                  }`}
                          loading="lazy"
                />
              </div>
            ) : (
              <div className={styles.empty}>{t('app.pages_not_found')}</div>
            )}
          </div>
        </div>
        <div className={styles.footer}>
          <button
            className={styles.nav}
            onClick={handlePrev}
            type="button"
            disabled={!hasPrev}
            aria-label={t('app.prev_page')}
          >
            ←
          </button>
          <span className={styles.counter}>
            {pages.length > 0 ? `${pageIndex + 1} / ${pages.length}` : "0 / 0"}
          </span>
          <button
            className={styles.nav}
            onClick={handleNext}
            type="button"
            disabled={!hasNext}
            aria-label={t('app.next_page')}
          >
            →
          </button>
        </div>
      </div>
    </div>
  );
};

export default BookPreviewModal;
